/**
 * Field operations data: checklists and incident reports.
 * Crew side (anyone whose login is linked to a crew record): the API's /crew routes.
 * Office side (directors and admins): reads under RLS, writes and files through the API's /admin routes.
 */
import { adminBlob, adminFetch } from "../../lib/api";
import { crewFetch } from "../crew-api";
import { PREVIEW } from "../../lib/preview";
import { supabase, type ChecklistKind, type ChecklistRecord, type Incident, type IncidentRow, type Injury, type IncidentKind, type ShiftChecklistRow } from "../../lib/supabase";

const previewOnly = (): never => {
  throw new Error("Not available in preview mode.");
};

/** Answers as the API takes them, by item id. */
export interface AnswerInput {
  answer?: "yes" | "no" | null;
  note?: string | null;
  time?: string | null;
  signoff?: { name: string; signature: string; hoursConfirmed: string } | null;
  finishTimes?: { crewMemberId: string; finishedAt: string }[] | null;
  text?: string | null;
}
export type Answers = Record<string, AnswerInput>;

/* ---------- checklists (crew) ---------- */

export async function saveChecklistDraft(bookingId: string, kind: ChecklistKind, answers: Answers): Promise<ChecklistRecord> {
  if (PREVIEW) return previewOnly();
  return (await crewFetch<{ checklist: ChecklistRecord }>(`/shifts/${bookingId}/checklists/${kind}`, { method: "PUT", body: JSON.stringify({ answers }) })).checklist;
}

export async function submitChecklist(bookingId: string, kind: ChecklistKind, answers: Answers, correction?: { replaces: string; reason: string }): Promise<ChecklistRecord> {
  if (PREVIEW) return previewOnly();
  const body = { answers, ...(correction ? { replaces: correction.replaces, correctionReason: correction.reason } : {}) };
  return (await crewFetch<{ checklist: ChecklistRecord }>(`/shifts/${bookingId}/checklists/${kind}/submit`, { method: "POST", body: JSON.stringify(body) })).checklist;
}

/* ---------- checklists (office) ---------- */

export async function checklistsForShifts(bookingIds: string[]): Promise<ShiftChecklistRow[]> {
  if (PREVIEW || !bookingIds.length) return [];
  const { data, error } = await supabase
    .from("shift_checklists")
    .select("id, booking_id, kind, replaces, correction_reason, items, context, crew_member_id, started_at, submitted_at, updated_at")
    .in("booking_id", bookingIds)
    .order("started_at");
  if (error) throw error;
  return data as ShiftChecklistRow[];
}

/** The record in force for a shift and kind: the end of the correction chain. */
export function currentRow(rows: ShiftChecklistRow[], bookingId: string, kind: ChecklistKind): ShiftChecklistRow | null {
  const mine = rows.filter((r) => r.booking_id === bookingId && r.kind === kind);
  const replaced = new Set(mine.map((r) => r.replaces).filter(Boolean));
  return mine.find((r) => !replaced.has(r.id)) ?? null;
}

export const isEndChecklistRequired = (e: unknown) => /END_CHECKLIST_REQUIRED/.test(e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e));

/** Mark a shift completed without its end of shift checklist. The reason, who and when are recorded. */
export async function completeWithoutChecklist(bookingId: string, reason: string): Promise<void> {
  if (PREVIEW) return previewOnly();
  const { error } = await supabase.from("bookings").update({ status: "completed", complete_override_reason: reason.trim() }).eq("id", bookingId);
  if (error) throw error;
}

/* ---------- incident reports ---------- */

export interface IncidentInput {
  kind: IncidentKind;
  occurredAt: string;
  location?: string;
  description: string;
  peopleInvolved?: string;
  injury: Injury;
  immediateAction?: string;
  category?: string;
  categoryOther?: string;
  notifiable: boolean;
}

export async function reportFromShift(bookingId: string, input: IncidentInput): Promise<{ incident: Incident; officeAlerted: boolean }> {
  if (PREVIEW) return previewOnly();
  return crewFetch(`/shifts/${bookingId}/incidents`, { method: "POST", body: JSON.stringify(input) });
}

export async function reportFromOffice(input: IncidentInput & { bookingId?: string; jobId?: string }): Promise<{ incident: Incident; officeAlerted: boolean }> {
  if (PREVIEW) return previewOnly();
  return adminFetch("/incidents", { method: "POST", body: JSON.stringify(input) });
}

/** Tick or untick a prompt. `as` says whose route: the reporter's (crew) or the office's. */
export async function tickPrompt(as: "crew" | "office", incidentId: string, promptId: string, done: boolean): Promise<Incident> {
  if (PREVIEW) return previewOnly();
  const f = as === "crew" ? crewFetch : adminFetch;
  return (await f<{ incident: Incident }>(`/incidents/${incidentId}/prompts/${promptId}`, { method: "POST", body: JSON.stringify({ done }) })).incident;
}

export async function addIncidentPhoto(as: "crew" | "office", incidentId: string, file: File): Promise<void> {
  if (PREVIEW) return previewOnly();
  const form = new FormData();
  form.set("file", file);
  const f = as === "crew" ? crewFetch : adminFetch;
  await f(`/incidents/${incidentId}/photos`, { method: "POST", body: form }, { timeoutMs: 120_000 });
}

const INCIDENT_SELECT = "*, jobs(reference, title), incident_photos(id, file_name, content_type, created_at)";

export async function listIncidents(): Promise<IncidentRow[]> {
  if (PREVIEW) return [];
  const { data, error } = await supabase.from("incidents").select(INCIDENT_SELECT).order("reported_at", { ascending: false }).limit(500);
  if (error) throw error;
  return data as IncidentRow[];
}

export async function getIncident(id: string): Promise<IncidentRow | null> {
  if (PREVIEW) return previewOnly();
  const { data, error } = await supabase.from("incidents").select(INCIDENT_SELECT).eq("id", id).maybeSingle();
  if (error) throw error;
  return data as IncidentRow | null;
}

export async function updateIncident(id: string, patch: { status?: "open" | "closed"; officeNotes?: string | null; insurerNotifiedOn?: string | null }): Promise<void> {
  if (PREVIEW) return previewOnly();
  await adminFetch(`/incidents/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
}

/** The other end of a reclassification: the incident a near miss became, or the near miss an incident came from. */
export async function reclassificationLink(row: Pick<IncidentRow, "id" | "kind" | "reclassified_from">): Promise<{ id: string; reference: string } | null> {
  if (PREVIEW) return null;
  const q = supabase.from("incidents").select("id, reference");
  const { data, error } = row.kind === "near_miss" ? await q.eq("reclassified_from", row.id).maybeSingle() : row.reclassified_from ? await q.eq("id", row.reclassified_from).maybeSingle() : { data: null, error: null };
  if (error) throw error;
  return data as { id: string; reference: string } | null;
}

/** Reclassify a near miss as an incident (office). Returns the new incident. */
export async function reclassifyNearMiss(id: string, body: { reason: string; injury: Injury; notifiable: boolean }): Promise<Incident> {
  if (PREVIEW) return previewOnly();
  return (await adminFetch<{ incident: Incident }>(`/incidents/${id}/reclassify`, { method: "POST", body: JSON.stringify(body) })).incident;
}

export async function incidentPhotoBlob(incidentId: string, photoId: string): Promise<Blob> {
  if (PREVIEW) return previewOnly();
  return adminBlob(`/incidents/${incidentId}/photos/${photoId}`, {}, { timeoutMs: 60_000 });
}

/** Whether the signed-in user is linked to a crew record (so they get "My shifts"). */
export async function myCrewRecord(userId: string): Promise<{ id: string; full_name: string } | null> {
  if (PREVIEW) return null;
  const { data, error } = await supabase.from("crew_members").select("id, full_name").eq("profile_id", userId).eq("active", true).maybeSingle();
  if (error) throw error;
  return data as { id: string; full_name: string } | null;
}
